import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { formatDate } from '../utils/formatDate';
import { DEMO_TICKETS } from '../demoData';

function DemoTicketChat({ ticketId, onBack }) {
  const ticket = DEMO_TICKETS.find((t) => t.id === Number(ticketId));
  const [reply, setReply] = useState('');
  const [messages, setMessages] = useState(ticket ? ticket.messages : []);
  const [sent, setSent] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!ticket) return <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>Ticket not found.</p>;

  const statusColor = { open: 'badge-warning', pending: 'badge-info', resolved: 'badge-success', closed: 'badge-purple' };

  const handleReply = (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setMessages((prev) => [
      ...prev,
      { id: Date.now(), senderType: 'user', message: reply, createdAt: new Date().toISOString() },
    ]);
    setReply('');
    setSent(true);
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          senderType: 'admin',
          message: "Thanks for reaching out! Our support team will get back to you shortly. In the meantime, feel free to explore the app — this is a demo account so everything is safe to try.",
          createdAt: new Date().toISOString(),
        },
      ]);
    }, 1200);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)', minHeight: 400 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, flexShrink: 0 }}>
        <div>
          <h2 style={{ fontSize: 18, marginBottom: 4 }}>#{ticket.id} — {ticket.subject}</h2>
          <span className={`badge ${statusColor[ticket.status] || 'badge-info'}`} style={{ textTransform: 'capitalize' }}>{ticket.status}</span>
        </div>
      </div>

      <div style={{ background: 'var(--bg)', borderRadius: 12, border: '1px solid var(--border)', padding: 20, flex: 1, overflowY: 'auto', marginBottom: 16, minHeight: 0 }}>
        {messages.map((msg) => (
          <div key={msg.id} style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', alignItems: msg.senderType === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{
              maxWidth: '80%',
              padding: '10px 14px',
              borderRadius: 12,
              background: msg.senderType === 'user' ? 'var(--primary)' : 'var(--surface)',
              color: msg.senderType === 'user' ? '#fff' : 'var(--text)',
              border: msg.senderType === 'admin' ? '1px solid var(--border)' : 'none',
              boxShadow: 'var(--shadow-sm)',
            }}>
              <p style={{ fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', margin: 0 }}>{msg.message}</p>
            </div>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, padding: '0 4px' }}>
              {msg.senderType === 'admin' ? 'Support Team' : 'You'} · {formatDate(msg.createdAt)}
            </span>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleReply} style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
        <input
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Type your reply..."
          style={{ flex: 1, padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 14, fontFamily: 'inherit' }}
        />
        <button type="submit" disabled={!reply.trim()} className="btn btn-primary">Send</button>
      </form>
    </div>
  );
}

function DemoCreateTicketForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({ subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!form.subject.trim() || form.subject.trim().length < 3) return setError('Please enter a subject (at least 3 characters)');
    if (!form.message.trim() || form.message.trim().length < 10) return setError('Please provide more details (at least 10 characters)');
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      onCreated({ subject: form.subject, message: form.message });
    }, 800);
  };

  return (
    <div className="card" style={{ maxWidth: 600 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700 }}>New Ticket</h2>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 18 }}>&times;</button>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Subject *</label>
          <input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} placeholder="Brief description of your issue" required />
        </div>
        <div className="form-group">
          <label>Message *</label>
          <textarea
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            placeholder="Please describe your issue in detail..."
            rows={8}
            required
            style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: 'var(--text)', background: 'var(--surface)', resize: 'vertical', minHeight: 180 }}
          />
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? <span className="spinner" /> : 'Submit Ticket'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </div>
  );
}


function TicketChat({ ticketId }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const { user } = useAuth();

  const loadTicket = async () => {
    try {
      const data = await api.getTicket(ticketId);
      setTicket(data.ticket);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTicket(); }, [ticketId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticket?.messages]);

  useEffect(() => {
    const poll = () => {
      if (document.visibilityState === 'visible') loadTicket();
    };
    const interval = setInterval(poll, 30000);
    return () => clearInterval(interval);
  }, [ticketId]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.replyToTicket(ticketId, reply);
      setReply('');
      loadTicket();
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  if (loading) return (
    <div style={{ maxWidth: 700 }}>
      <button onClick={() => navigate('/my-tickets')} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, marginBottom: 16, padding: 0 }}>
        &larr; All Tickets
      </button>
      <div style={{ padding: 40, textAlign: 'center' }}><span className="spinner" /></div>
    </div>
  );
  if (!ticket) return <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>Ticket not found.</p>;

  const statusColor = { open: 'badge-warning', pending: 'badge-info', resolved: 'badge-success', closed: 'badge-purple' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)', minHeight: 400 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, flexShrink: 0 }}>
        <div>
          <h2 style={{ fontSize: 18, marginBottom: 4 }}>#{ticket.id} — {ticket.subject}</h2>
          <span className={`badge ${statusColor[ticket.status] || 'badge-info'}`} style={{ textTransform: 'capitalize' }}>{ticket.status}</span>
        </div>
      </div>

      {/* Chat messages */}
      <div style={{ background: 'var(--bg)', borderRadius: 12, border: '1px solid var(--border)', padding: 20, flex: 1, overflowY: 'auto', marginBottom: 16, minHeight: 0 }}>
        {ticket.messages && ticket.messages.map((msg) => (
          <div key={msg.id} style={{
            marginBottom: 12,
            display: 'flex',
            flexDirection: 'column',
            alignItems: msg.senderType === 'user' ? 'flex-end' : 'flex-start',
          }}>
            <div style={{
              maxWidth: '80%',
              padding: '10px 14px',
              borderRadius: 12,
              background: msg.senderType === 'user' ? 'var(--primary)' : 'var(--surface)',
              color: msg.senderType === 'user' ? '#fff' : 'var(--text)',
              border: msg.senderType === 'admin' ? '1px solid var(--border)' : 'none',
              boxShadow: 'var(--shadow-sm)',
            }}>
              <p style={{ fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', margin: 0 }}>{msg.message}</p>
            </div>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, padding: '0 4px' }}>
              {msg.senderType === 'admin' ? 'Support Team' : 'You'} · {formatDate(msg.createdAt)}
            </span>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply form */}
      {ticket.status !== 'closed' && (
        <form onSubmit={handleReply} style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
          <input
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Type your reply..."
            style={{ flex: 1, padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 14, fontFamily: 'inherit' }}
          />
          <button type="submit" disabled={sending || !reply.trim()} className="btn btn-primary">
            {sending ? <span className="spinner" /> : 'Send'}
          </button>
        </form>
      )}
      {ticket.status === 'closed' && (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', flexShrink: 0 }}>This ticket is closed.</p>
      )}
    </div>
  );
}

function CreateTicketForm({ onCreated, onCancel }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.subject.trim() || form.subject.trim().length < 3) {
      return setError('Please enter a subject (at least 3 characters)');
    }
    if (!form.message.trim() || form.message.trim().length < 10) {
      return setError('Please provide more details (at least 10 characters)');
    }
    setLoading(true);
    try {
      await api.createSupportTicket({
        name: user?.email?.split('@')[0] || 'User',
        email: user?.email || '',
        subject: form.subject,
        message: form.message,
      });
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card" style={{ maxWidth: 600 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700 }}>New Ticket</h2>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 18 }}>&times;</button>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Subject *</label>
          <input
            value={form.subject}
            onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
            placeholder="Brief description of your issue"
            required
          />
        </div>
        <div className="form-group">
          <label>Message *</label>
          <textarea
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            placeholder="Please describe your issue in detail..."
            rows={8}
            required
            style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: 'var(--text)', background: 'var(--surface)', resize: 'vertical', minHeight: 180 }}
          />
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? <span className="spinner" /> : 'Submit Ticket'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

export default function MyTickets() {
  const { id } = useParams();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const navigate = useNavigate();
  const { isDemo } = useAuth();

  const [demoTickets, setDemoTickets] = useState(DEMO_TICKETS);
  const [demoViewId, setDemoViewId] = useState(null);
  const [demoShowCreate, setDemoShowCreate] = useState(false);

  const loadTickets = () => {
    api.getMyTickets().then((data) => setTickets(data.tickets)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isDemo) {
      setLoading(false);
      return;
    }
    if (!id) {
      setLoading(true);
      loadTickets();
    }
  }, [id, isDemo]);

  /* ── Demo mode ── */
  if (isDemo) {
    if (demoViewId !== null) {
      return (
        <div style={{ maxWidth: 700 }}>
          <button onClick={() => setDemoViewId(null)} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, marginBottom: 16, padding: 0 }}>
            &larr; All Tickets
          </button>
          <DemoTicketChat ticketId={demoViewId} onBack={() => setDemoViewId(null)} />
        </div>
      );
    }

    if (demoShowCreate) {
      return (
        <div style={{ maxWidth: 700 }}>
          <DemoCreateTicketForm
            onCreated={({ subject, message }) => {
              const newTicket = {
                id: Math.floor(Math.random() * 900) + 100,
                subject,
                status: 'open',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                messages: [{ id: Date.now(), senderType: 'user', message, createdAt: new Date().toISOString() }],
              };
              setDemoTickets((prev) => [newTicket, ...prev]);
              setDemoShowCreate(false);
            }}
            onCancel={() => setDemoShowCreate(false)}
          />
        </div>
      );
    }

    const statusColor = { open: 'badge-warning', pending: 'badge-info', resolved: 'badge-success', closed: 'badge-purple' };
    return (
      <div style={{ maxWidth: 700 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700 }}>Support</h1>
          <button onClick={() => setDemoShowCreate(true)} className="btn btn-primary btn-sm">New Ticket</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {demoTickets.map((t) => {
            const lastMsg = t.messages && t.messages[t.messages.length - 1];
            return (
              <div key={t.id} onClick={() => setDemoViewId(t.id)} className="card" style={{ cursor: 'pointer', transition: 'all 0.15s' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>#{t.id} — {t.subject}</h3>
                    {lastMsg && (
                      <p style={{ fontSize: 13, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 400 }}>
                        {lastMsg.senderType === 'admin' ? 'Support: ' : 'You: '}{lastMsg.message}
                      </p>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                    <span className={`badge ${statusColor[t.status] || 'badge-info'}`} style={{ textTransform: 'capitalize' }}>{t.status}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatDate(t.updatedAt)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  /* ── End demo mode ── */

  if (id) {
    return (
      <div style={{ maxWidth: 700 }}>
        <button onClick={() => navigate('/my-tickets')} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, marginBottom: 16, padding: 0 }}>
          &larr; All Tickets
        </button>
        <TicketChat ticketId={id} />
      </div>
    );
  }

  if (showCreate) {
    return (
      <div style={{ maxWidth: 700 }}>
        <CreateTicketForm
          onCreated={() => { setShowCreate(false); loadTickets(); }}
          onCancel={() => setShowCreate(false)}
        />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 700 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>Support</h1>
        <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm">New Ticket</button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[1, 2, 3].map(i => (
            <div key={i} className="card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ height: 16, width: '50%', background: 'var(--bg)', borderRadius: 4, marginBottom: 10, animation: 'pulse 1.5s ease-in-out infinite' }} />
                  <div style={{ height: 12, width: '70%', background: 'var(--bg)', borderRadius: 4, marginBottom: 8, animation: 'pulse 1.5s ease-in-out infinite' }} />
                  <div style={{ height: 11, width: 100, background: 'var(--bg)', borderRadius: 4, animation: 'pulse 1.5s ease-in-out infinite' }} />
                </div>
                <div style={{ height: 22, width: 60, background: 'var(--bg)', borderRadius: 12, animation: 'pulse 1.5s ease-in-out infinite' }} />
              </div>
            </div>
          ))}
        </div>
      ) : tickets.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">💬</div>
            <h3>No tickets yet</h3>
            <p style={{ marginBottom: 16 }}>You haven't raised any support tickets.</p>
            <button onClick={() => setShowCreate(true)} className="btn btn-primary">Create a Ticket</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {tickets.map((t) => {
              const statusColor = { open: 'badge-warning', pending: 'badge-info', resolved: 'badge-success', closed: 'badge-purple' };
              const lastMsg = t.messages && t.messages[0];
              return (
                <div key={t.id} onClick={() => navigate(`/my-tickets/${t.id}`)} className="card" style={{ cursor: 'pointer', transition: 'all 0.15s' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>#{t.id} — {t.subject}</h3>
                      {lastMsg && (
                        <p style={{ fontSize: 13, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 400 }}>
                          {lastMsg.senderType === 'admin' ? 'Support: ' : 'You: '}{lastMsg.message}
                        </p>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                      <span className={`badge ${statusColor[t.status] || 'badge-info'}`} style={{ textTransform: 'capitalize' }}>{t.status}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatDate(t.updatedAt)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
